const post = {
  slug: 'first-20-youtube-videos',
  cluster: 'strategy',
  eyebrow: 'Strategy',
  title: 'What your first 20 videos are actually for',
  description:
    'They are not for growth. They are for finding out what your channel is — and there is a specific way to make them count.',
  keywords: [
    'first youtube videos', 'starting a youtube channel', 'new channel strategy',
    'youtube channel growth', 'first 20 videos', 'faceless youtube channel',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/first-20-youtube-videos.webp',
    alt: 'A grid of twenty numbered video thumbnails, with a few marked as the ones that found an audience.',
  },
  lede:
    'The first twenty videos are a search problem, not a growth problem. You are looking for which version of your channel people want — and the fastest way to fail is to optimise for views before you have found it.',
  sections: [
    {
      heading: 'Why twenty',
      body: [
        {
          type: 'para',
          text: 'It is not a magic threshold. It is roughly the point where you have enough videos that patterns stop being noise.',
        },
        {
          type: 'para',
          text: 'With five videos, one doing four times the others tells you almost nothing — that happens by chance constantly. With twenty, if three cluster clearly above the rest and they share something, that shared thing is real information.',
        },
        {
          type: 'para',
          text: 'Everything before that point is data collection. Treating it as performance is how people quit at video seven.',
        },
      ],
    },
    {
      heading: 'Make them comparable',
      body: [
        {
          type: 'para',
          text: 'The mistake that wastes the whole exercise: changing everything at once. New format, new thumbnail style, new length, new topic area — and then no way to tell which change mattered.',
        },
        {
          type: 'para',
          text: 'Hold the format constant and vary the topic. That way, when something outperforms, you learn about the *subject matter*, which is the thing you most need to know early. Format experiments come later, once you know what you are about.',
        },
        {
          type: 'callout',
          title: 'One deliberate exception',
          text: 'Thumbnails are worth iterating on continuously, because they affect whether the video is opened at all. A video nobody clicks tells you nothing about whether the topic was good.',
        },
      ],
    },
    {
      heading: 'What to actually look at',
      body: [
        {
          type: 'para',
          text: 'Two numbers matter early, and neither is view count.',
        },
        {
          type: 'list',
          items: [
            '**Click-through rate** tells you whether the packaging works — the title and thumbnail. A low CTR on a good video means people never found out it was good.',
            '**Retention shape** tells you whether the video works. Not the average figure: the *shape*. Where does it drop? A cliff in the first thirty seconds means the opening broke a promise the thumbnail made. A slow bleed from the middle means nothing was pending.',
          ],
        },
        {
          type: 'para',
          text: 'View count is downstream of both and tells you nothing actionable on its own. A video with 200 views and 60% retention is a better signal than one with 2,000 views and 20%.',
        },
      ],
    },
    {
      heading: 'The twenty topics',
      body: [
        {
          type: 'para',
          text: 'Write the list before you make the first one. This is the single highest-leverage hour in the whole process, for two reasons.',
        },
        {
          type: 'para',
          text: 'First, if you cannot list twenty topics you would genuinely want to research, the niche is wrong — either too narrow to sustain, or not actually interesting to you. Finding that out now costs an hour. Finding it out at video eleven costs months.',
        },
        {
          type: 'para',
          text: 'Second, having the list removes the per-video decision that stalls people. The hardest part of publishing consistently is not making the video; it is deciding what the video is. [Topic research](/blogs/youtube-topic-research) covers how to build the list.',
        },
      ],
    },
    {
      heading: 'Deliberately do not do these',
      body: [
        {
          type: 'list',
          items: [
            '**Do not delete underperformers.** They cost nothing to leave up, they occasionally find an audience a year later, and deleting them destroys the comparison set you are trying to build.',
            '**Do not redesign the channel.** Banner, trailer, about page — none of this is why a video did not get watched.',
            '**Do not chase a trend outside your niche.** A hit that has nothing to do with what you make brings subscribers who will not watch the next one, which makes your numbers worse, not better.',
            '**Do not restart the channel.** Starting over discards the only asset you have built, which is data.',
          ],
        },
      ],
    },
    {
      heading: 'What "working" looks like at twenty',
      body: [
        {
          type: 'para',
          text: 'Not a large channel. What you are looking for is much smaller and more specific: **two or three videos that clearly outperformed the rest, and an identifiable reason why.**',
        },
        {
          type: 'para',
          text: 'That is the finding. The next twenty videos are then variations on it, rather than another twenty guesses. If instead everything performed the same — uniformly low — the problem is usually upstream of the videos: either the packaging never earned a click, or the niche has no search demand behind it.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'How long should the first twenty take?',
      a: 'Long enough to make each one properly, short enough that you learn something while it is still relevant. A weekly cadence puts you at five months, which is reasonable. Two a week is fine if the quality holds and punishing if it does not.',
    },
    {
      q: 'What if none of the twenty do anything?',
      a: 'Check click-through rate first. Uniformly low CTR is a packaging problem and is very fixable. Decent CTR with poor retention is a script problem. Both low, on topics that clearly have demand, usually means the topics were generic rather than the execution being bad.',
    },
  ],
  related: ['youtube-channel-not-growing', 'youtube-posting-schedule', 'youtube-topic-research'],
};

export default post;
